import parse from 'html-react-parser';
import { Component, createRef } from 'react';
import QuickReplyList from '../../containers/QuickReplyList';
import { sendNavigateToUrl } from '../../services/postMessageService';
import type { Button, MessageWithButtonsData } from '../../types/ChatWindow';
import type { MessageScheme } from '../../types/configs';
import { purifyAndLinkifyText } from './messageHelper';

class ButtonedTextMessage extends Component<
  {
    data: MessageWithButtonsData;
    scheme: MessageScheme;
    submitHandler: (...args: any[]) => void;
    isLastMessage: boolean;
    suppressAnimation?: boolean;
  },
  { buttonClicked: boolean }
> {
  textContainerRef = createRef<HTMLDivElement>();

  constructor(props: {
    data: MessageWithButtonsData;
    scheme: MessageScheme;
    submitHandler: (...args: any[]) => void;
    isLastMessage: boolean;
    suppressAnimation?: boolean;
  }) {
    super(props);
    this.state = {
      buttonClicked: false,
    };
  }

  componentDidMount() {
    const container = this.textContainerRef.current;

    if (!container) {
      return;
    }

    container.addEventListener('click', this.handleCollapsibleClick);
  }

  componentWillUnmount() {
    const container = this.textContainerRef.current;

    if (!container) {
      return;
    }

    container.removeEventListener('click', this.handleCollapsibleClick);
  }

  handleCollapsibleClick = (event: MouseEvent) => {
    const summary = (event.target as Element).closest(
      'details.conversed-collapsible > summary',
    );

    if (!summary) {
      return;
    }

    const details = summary.closest(
      'details.conversed-collapsible',
    ) as HTMLDetailsElement;

    // * The toggle default action runs after click handlers, so defer the
    // * open check until after the browser has updated details.open.
    requestAnimationFrame(() => {
      if (details.open) {
        summary.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  };

  handleClick = (clickedButton: Button) => {
    const {
      submitHandler,
      data: { llmUsageId },
    } = this.props;

    // * Shortcut buttons send their label text as plain user input so the destination
    // * block (typically LLM) receives it as the question without waiting for fresh input.
    if (clickedButton.isShortcut) {
      this.setState({ buttonClicked: true });
      return submitHandler({ value: clickedButton.title });
    }

    const submitData = {
      value: clickedButton,
    };

    // Examples:
    // 1. A button that should navigate to a specific link when clicked.
    // 2. A button that should close the window without navigating when clicked.
    if (
      clickedButton.url ||
      clickedButton?.options?.webclient?.keepButtonVisibleAfterClick
    ) {
      if (clickedButton.url) {
        sendNavigateToUrl(
          clickedButton.url,
          clickedButton?.options?.webclient?.urlOpenMethod,
        );
      }

      const value = {
        ...submitData.value,
      };

      if (llmUsageId) {
        Object.assign(value, {
          llmUsageId,
        });
      }

      Object.assign(submitData, {
        value,
        postbackType: 'buttonClick',
      });

      return submitHandler({ ...submitData, shouldHideMessage: true });
    }

    // If the button is not a URL button, for example it is a Menu button, then we will show the button as clicked.
    // Convert the clicked button to a text and do not show the button anymore.
    this.setState({
      buttonClicked: true,
    });
    return submitHandler({ ...submitData });
  };

  render() {
    const { buttonClicked } = this.state;
    const {
      data: { text, buttons },
      scheme: { messageBubbleColor, messageBubbleTextColor },
      scheme,
      isLastMessage,
    } = this.props;

    const { suppressAnimation } = this.props;
    const message = purifyAndLinkifyText(text);
    const showText = typeof text === 'string' && text !== '';

    return (
      <>
        {showText && (
          <div
            ref={this.textContainerRef}
            className={`word-break-style message-content${suppressAnimation ? ' animation-none' : ''}`}
            style={{
              backgroundColor: messageBubbleColor,
              color: messageBubbleTextColor,
            }}>
            {parse(message)}
          </div>
        )}
        {!buttonClicked && isLastMessage && buttons?.length > 0 && (
          <QuickReplyList
            buttons={buttons}
            onClick={this.handleClick}
            scheme={scheme}
          />
        )}
      </>
    );
  }
}

export default ButtonedTextMessage;
